import React, { useState } from 'react';
import {
  GUITAR_CHORDS,
  GUITAR_STRINGS,
  FOUR_CHORD_PROGRESSIONS,
  ChordData,
  ChordProgressionLevel,
  getChordFrequencies,
} from '../../utils/guitarTheory';
import { soundEngine } from '../../utils/audio';
import { Sparkles, Music, Volume2, BookOpen, ArrowRight, Play, Info } from 'lucide-react';
import confetti from 'canvas-confetti';
import { AllChordsEncyclopedia } from './AllChordsEncyclopedia';

export const ChordStrummer: React.FC = () => {
  // View toggle: 'progression' (Part 1) or 'encyclopedia' (Part 2)
  const [view, setView] = useState<'progression' | 'encyclopedia'>('progression');

  // Part 1: Selected 4-chord progression level (Level 1 by default)
  const [selectedLevel, setSelectedLevel] = useState<ChordProgressionLevel>(FOUR_CHORD_PROGRESSIONS[0]);

  // Current chord selected for individual display (defaults to first chord of Level 1)
  const [selectedChordId, setSelectedChordId] = useState<string>(FOUR_CHORD_PROGRESSIONS[0].chordIds[0]);
  const [vibratingString, setVibratingString] = useState<number | null>(null);
  const [isStrumming, setIsStrumming] = useState(false);
  const [playingProgressionIdx, setPlayingProgressionIdx] = useState<number | null>(null);

  // Active chord data
  const selectedChord = GUITAR_CHORDS.find((c) => c.id === selectedChordId) || GUITAR_CHORDS[0];

  // Pluck an individual string
  const handlePluckString = (stringIndex: number) => {
    const fret = selectedChord.frets[stringIndex];
    if (fret === -1) {
      soundEngine.playIncorrectSfx();
      return;
    }
    const str = GUITAR_STRINGS[stringIndex];
    const freq = str.baseFreq * Math.pow(2, fret / 12);

    setVibratingString(stringIndex);
    soundEngine.playGuitarPluck(freq, 2.4, 0.55);
    setTimeout(() => setVibratingString(null), 500);
  };

  // Strum the current single chord
  const handleStrum = () => {
    setIsStrumming(true);
    soundEngine.playPopSfx();
    const freqs = getChordFrequencies(selectedChord);
    soundEngine.strumChord(freqs, 40);

    confetti({
      particleCount: 20,
      spread: 50,
      origin: { y: 0.7 },
      colors: ['#F472B6', '#C084FC', '#FDE047'],
    });

    setTimeout(() => setIsStrumming(false), 600);
  };

  // Play the entire 4-chord progression in musical sequence
  const handlePlayProgressionSequence = () => {
    if (playingProgressionIdx !== null) return;
    soundEngine.playPopSfx();

    const chordsInSequence = selectedLevel.chordIds.map(
      (id) => GUITAR_CHORDS.find((c) => c.id === id) || GUITAR_CHORDS[0]
    );

    chordsInSequence.forEach((chord, i) => {
      setTimeout(() => {
        setPlayingProgressionIdx(i);
        setSelectedChordId(chord.id);
        const freqs = getChordFrequencies(chord);
        soundEngine.strumChord(freqs, 35);

        if (i === chordsInSequence.length - 1) {
          confetti({
            particleCount: 30,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#F472B6', '#C084FC', '#38BDF8'],
          });
          setTimeout(() => setPlayingProgressionIdx(null), 1200);
        }
      }, i * 1100);
    });
  };

  // If user opened Part 2 (Encyclopedia)
  if (view === 'encyclopedia') {
    return (
      <AllChordsEncyclopedia
        onBackToProgressions={() => setView('progression')}
        initialChordId={selectedChordId}
      />
    );
  }

  // --- PART 1: 4-Chord Progression Introduction ---
  return (
    <div className="space-y-8 animate-fadeIn">
      
      {/* Part 1 Header: 4-Level Beginner Progression Introduction */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-pink-200 shadow-md space-y-6">
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-pink-100 pb-5">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-100 text-pink-700 font-bold text-xs uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Part 1 • 4-Chord Beginner Progression</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-800">
              Master Songs Fast with 4 Beginner Levels
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm max-w-xl">
              Choose a level from easiest to more challenging. Real hit songs are built by looping these 4 chords together!
            </p>
          </div>

          {/* Link to Part 2: Complete Chord Encyclopedia */}
          <button
            onClick={() => {
              soundEngine.playPopSfx();
              setView('encyclopedia');
            }}
            className="px-5 py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-xs sm:text-sm shadow-md hover:scale-105 active:scale-95 transition-all flex items-center gap-2 self-start md:self-auto cursor-pointer"
          >
            <BookOpen className="w-4 h-4" />
            <span>Go to All Chords Encyclopedia 📚 →</span>
          </button>
        </div>

        {/* 4 Progression Level Selector Tabs */}
        <div>
          <label className="text-xs font-bold text-purple-900 block mb-2.5">
            Select Your 4-Chord Progression Level:
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {FOUR_CHORD_PROGRESSIONS.map((prog) => {
              const isSelected = selectedLevel.level === prog.level;
              return (
                <button
                  key={prog.level}
                  onClick={() => {
                    soundEngine.playPopSfx();
                    setSelectedLevel(prog);
                    setSelectedChordId(prog.chordIds[0]);
                  }}
                  className={`p-4 rounded-2xl border-2 text-left transition-all cursor-pointer relative ${
                    isSelected
                      ? 'border-pink-500 bg-gradient-to-br from-pink-50 to-purple-50 ring-2 ring-pink-200 shadow-sm scale-102 font-bold'
                      : 'border-pink-100 bg-white hover:border-pink-300 hover:bg-pink-50/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-extrabold text-purple-800">
                      Level {prog.level}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/80 border border-pink-200 text-pink-700 font-semibold">
                      {prog.level === 1 ? 'Easiest' : prog.level === 4 ? 'Advanced' : 'Beginner'}
                    </span>
                  </div>
                  <div className="text-sm font-extrabold text-gray-800 line-clamp-1">{prog.title}</div>
                  <div className="text-[11px] text-pink-600 mt-1">{prog.kawaiiMood}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Level Highlight Banner with Chord Sequence */}
        <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-pink-50 via-purple-50 to-rose-50 border-2 border-pink-200 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <span className="text-xs font-extrabold text-pink-700 bg-pink-100/80 px-2.5 py-0.5 rounded-full">
                {selectedLevel.badge}
              </span>
              <h4 className="text-lg sm:text-xl font-extrabold text-purple-950 mt-1">
                {selectedLevel.title}
              </h4>
              <p className="text-xs text-purple-900/80 mt-0.5 font-medium">
                {selectedLevel.description} — <i>Famous for: {selectedLevel.famousFor}</i>
              </p>
            </div>

            <button
              onClick={handlePlayProgressionSequence}
              disabled={playingProgressionIdx !== null}
              className={`px-5 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 via-rose-500 to-purple-500 text-white font-bold text-xs shadow-md hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer self-start sm:self-auto shrink-0 ${
                playingProgressionIdx !== null ? 'animate-pulse opacity-80' : ''
              }`}
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>
                {playingProgressionIdx !== null
                  ? `Strumming #${playingProgressionIdx + 1}...`
                  : 'Play 4-Chord Sequence 🎶'}
              </span>
            </button>
          </div>

          {/* 4 Chords in Sequence Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            {selectedLevel.chordIds.map((chordId, idx) => {
              const chord = GUITAR_CHORDS.find((c) => c.id === chordId) || GUITAR_CHORDS[0];
              const isSelected = selectedChordId === chord.id;
              const isPlayingInLoop = playingProgressionIdx === idx;

              return (
                <button
                  key={chord.id}
                  onClick={() => {
                    soundEngine.playPopSfx();
                    setSelectedChordId(chord.id);
                  }}
                  className={`p-3.5 rounded-2xl border-2 text-center transition-all cursor-pointer relative ${
                    isPlayingInLoop
                      ? 'border-yellow-400 bg-yellow-100/90 ring-4 ring-yellow-200 scale-105 animate-bounce'
                      : isSelected
                      ? 'border-pink-500 bg-white ring-2 ring-pink-300 shadow-md scale-102'
                      : 'border-pink-200 bg-white/80 hover:border-pink-400 hover:bg-white'
                  }`}
                >
                  <span className="absolute top-2 left-2 text-[10px] font-bold text-purple-600 bg-purple-100/80 px-1.5 py-0.2 rounded-md">
                    #{idx + 1}
                  </span>

                  <div className="text-2xl font-extrabold text-purple-950 mt-1">{chord.symbol}</div>
                  <div className="text-xs text-pink-600 font-semibold truncate">{chord.name}</div>
                  <div className="text-[10px] text-gray-400 mt-1">{chord.difficulty}</div>
                </button>
              );
            })}
          </div>
        </div>

      </div>

      {/* Main Interactive Stage for Selected Chord */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left 7 cols: Graphical Interactive Chord Chart */}
        <div className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-8 border-2 border-pink-200 shadow-md">
          
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="px-3 py-1 rounded-full bg-pink-100 text-pink-700 font-bold text-xs uppercase">
                {selectedChord.category} Chord
              </span>
              <h3 className="text-3xl font-extrabold text-gray-800 mt-1">
                {selectedChord.name} ({selectedChord.symbol})
              </h3>
            </div>
            
            <button
              onClick={handleStrum}
              disabled={isStrumming}
              className={`px-6 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer ${
                isStrumming ? 'animate-bounce' : ''
              }`}
            >
              <Music className="w-4 h-4" />
              <span>Strum Chord! 🎶</span>
            </button>
          </div>

          <p className="text-xs text-gray-500 mb-6 flex items-center gap-1.5">
            <Volume2 className="w-3.5 h-3.5 text-pink-500" />
            <span>Click any individual string below to pluck it, or hit <b>Strum Chord</b>!</span>
          </p>

          {/* Graphical Chord Box */}
          <div className="max-w-sm mx-auto bg-amber-50/70 border-4 border-amber-800/60 rounded-2xl p-5 shadow-inner">
            
            {/* Top Nut / Mute-Open String Indicators */}
            <div className="grid grid-cols-6 text-center mb-2">
              {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                const fret = selectedChord.frets[strIdx];
                const isMute = fret === -1;
                const isOpen = fret === 0;
                return (
                  <div key={strIdx} className="font-extrabold text-sm sm:text-base">
                    {isMute ? (
                      <span className="text-red-500">✕</span>
                    ) : isOpen ? (
                      <span className="text-emerald-500">○</span>
                    ) : (
                      <span className="text-purple-400 text-xs">fret {fret}</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Nut Line */}
            <div className="h-2 bg-amber-900 rounded-sm mb-1" />

            {/* Frets Grid (Frets 1 to 4) */}
            <div className="relative border-x-2 border-b-2 border-amber-900/60 bg-amber-100/40 divide-y-2 divide-amber-900/50">
              {[1, 2, 3, 4].map((fretNum) => (
                <div key={fretNum} className="relative h-14 sm:h-16 flex items-center">
                  <span className="absolute -left-7 text-[11px] font-bold text-amber-900/70">
                    F{fretNum}
                  </span>

                  <div className="w-full grid grid-cols-6 h-full relative">
                    {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                      const chordFret = selectedChord.frets[strIdx];
                      const finger = selectedChord.fingers[strIdx];
                      const hasFingerOnThisFret = chordFret === fretNum;
                      const isVibrating = vibratingString === strIdx;

                      return (
                        <div
                          key={strIdx}
                          onClick={() => handlePluckString(strIdx)}
                          className="relative h-full flex items-center justify-center cursor-pointer group"
                        >
                          <div
                            className={`absolute top-0 bottom-0 transition-transform ${
                              isVibrating ? 'string-vibrating bg-pink-500' : 'bg-amber-800/80 group-hover:bg-pink-400'
                            }`}
                            style={{
                              width: `${Math.max(1.5, 4 - strIdx * 0.5)}px`,
                            }}
                          />

                          {hasFingerOnThisFret && (
                            <div className="relative z-10 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-tr from-pink-500 to-rose-400 text-white font-extrabold text-xs sm:text-sm flex items-center justify-center shadow-md animate-scaleIn border-2 border-white">
                              {finger > 0 ? finger : '●'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* String Notes Label Row */}
            <div className="grid grid-cols-6 text-center mt-3">
              {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                const str = GUITAR_STRINGS[strIdx];
                return (
                  <button
                    key={strIdx}
                    onClick={() => handlePluckString(strIdx)}
                    className="p-1 rounded-lg hover:bg-pink-100 transition-colors cursor-pointer"
                    title={`Click to pluck ${str.name}`}
                  >
                    <div className="text-xs font-bold text-gray-700">{str.baseNote}</div>
                    <div className="text-[10px] text-gray-400">{strIdx === 0 ? 'High' : strIdx === 5 ? 'Low' : ''}</div>
                  </button>
                );
              })}
            </div>

          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-xs text-gray-600 bg-pink-50/50 p-2.5 rounded-xl border border-pink-100">
            <span className="font-bold text-purple-900">Finger Legend:</span>
            <span>① Index</span>
            <span>② Middle</span>
            <span>③ Ring</span>
            <span>④ Pinky</span>
          </div>

        </div>

        {/* Right 5 cols: Chord Info & Part 2 Link Card */}
        <div className="lg:col-span-5 space-y-4">
          
          {/* Kawaii Vibe Card */}
          <div className="rounded-3xl p-6 bg-gradient-to-br from-pink-100/90 via-purple-100/80 to-rose-100/90 border-2 border-pink-200 shadow-sm space-y-3">
            <div className="flex items-center gap-2 text-pink-700 font-bold text-sm">
              <Sparkles className="w-4 h-4" />
              <span>Kawaii Chord Mood</span>
            </div>
            <div className="text-xl font-bold text-purple-950">
              {selectedChord.kawaiiVibe}
            </div>
            <p className="text-sm text-purple-900/80">
              {selectedChord.description}
            </p>
          </div>

          {/* Part 2 Encyclopedia CTA Banner */}
          <div className="rounded-3xl p-6 bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-950 text-white shadow-lg space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-pink-300">
              <BookOpen className="w-4 h-4" />
              <span>Part 2 • Complete Library</span>
            </div>

            <div className="space-y-1">
              <h4 className="text-lg font-bold text-white">All Chords Encyclopedia 📚</h4>
              <p className="text-xs text-purple-200 leading-relaxed">
                Want to learn all chords beyond the 4-chord progressions? Explore chords classified systematically into <b>Major</b>, <b>Minor</b>, <b>7th</b>, <b>Diminished</b>, and <b>Suspended</b>!
              </p>
            </div>

            <button
              onClick={() => {
                soundEngine.playPopSfx();
                setView('encyclopedia');
              }}
              className="w-full py-3 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-500 to-purple-500 text-white font-bold text-xs sm:text-sm shadow-md hover:scale-102 active:scale-98 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Explore All Chords Page</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

        </div>

      </div>

    </div>
  );
};
